import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import PageContainer from '../../common_components/PageContainer';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import CommonTabs from '../../common_components/CommonTabs';
import CommonLoader from '../../common_components/CommonLoader';
import TableToolbar from '../../common_components/TableToolbar';
import AppButton from '../../common_components/AppButton';
import { toast } from '../../common_components/Toast/ToastUtils';
import { fetchReportById, refreshCurrentReport, updateReportThunk, generateDeckThunk, downloadDeckBlob, selectCurrentReport, selectCurrentReportLoading, selectCurrentReportError, selectGeneratingDeckReportId, clearCurrentReport, } from '../../redux/metaAdAuditSlice';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import ReportSwitcher from './components/ReportSwitcher';
import PeriodPicker from './components/PeriodPicker';
import CustomBenchmarkDrawer from './components/CustomBenchmarkDrawer';
import SummaryTab from './tabs/SummaryTab';
import AdClassificationsTab from './tabs/AdClassificationsTab';
import FailInsightsTab from './tabs/FailInsightsTab';
import LearningInsightsTab from './tabs/LearningInsightsTab';
import ProjectionsTab from './tabs/ProjectionsTab';
import ProjectionsV2Tab from './tabs/ProjectionsV2Tab';
import InsightsTab from './tabs/InsightsTab';
const MODULE_KEY = 'meta_ad_audit';
const DeckIconSvg = () => (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", children: [_jsx("path", { d: "M3 3h14v11H3V3z", stroke: "#52525b", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M7 17h6M10 14v3", stroke: "#52525b", strokeWidth: "1.2", strokeLinecap: "round" })] }));
const TABS = [
    { label: 'Summary', value: 'summary' },
    { label: 'Ad Classifications', value: 'classifications' },
    { label: 'Fail Insights', value: 'fail-insights' },
    { label: 'Learning Insights', value: 'learning' },
    { label: 'Projections', value: 'projections' },
    { label: 'Projections V2', value: 'projections-v2' },
    { label: 'Insights', value: 'audit-insights' },
];
const MetaAdAuditReport = () => {
    const { reportId = '' } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const report = useSelector(selectCurrentReport);
    const loading = useSelector(selectCurrentReportLoading);
    const error = useSelector(selectCurrentReportError);
    const generatingDeckReportId = useSelector(selectGeneratingDeckReportId);
    // const userPermissions = useSelector(selectUserPermissions);
    const userPermissions = useSelector((state) => state.auth.permissions);
    const authToken = useSelector((s) => s?.auth?.token);
    const modulePerms = userPermissions?.[MODULE_KEY];
    const canRefresh = !!userPermissions?.[MODULE_KEY]?.refresh;
    const canEdit = Array.isArray(userPermissions?.[MODULE_KEY]?.actions)
        ? userPermissions[MODULE_KEY].actions.includes('add_report')
        : false;
    const canGenerateDeck = Array.isArray(userPermissions?.[MODULE_KEY]?.actions)
        ? userPermissions[MODULE_KEY].actions.includes('generate_deck')
        : false;
    const [activeTab, setActiveTab] = useState('summary');
    const [refreshing, setRefreshing] = useState(false);
    const [applyingPeriod, setApplyingPeriod] = useState(false);
    const [benchmarkDrawerOpen, setBenchmarkDrawerOpen] = useState(false);
    const deckBusyFor = generatingDeckReportId;
    const deckBusy = deckBusyFor === report?.report_id;
    // ── Lifecycle ──────────────────────────────────────────────────────
    useEffect(() => {
        // Ensure the module key is set before we fire any report-specific APIs.
        dispatch(setActiveModuleKey(MODULE_KEY));
        if (reportId) {
            dispatch(fetchReportById(reportId));
        }
        // Cleanup only clears the report data, NOT the module key,
        // to avoid race conditions during navigation within the module.
        return () => {
            dispatch(clearCurrentReport());
        };
    }, [dispatch, reportId]);
    // ── Handlers ────────────────────────────────────────────────────────
    const handleBack = () => navigate('/meta-ad-audit');
    const handleSwitchReport = useCallback((newReportId) => {
        if (newReportId !== reportId) {
            setActiveTab('summary');
            navigate(`/meta-ad-audit/report/${newReportId}`);
        }
    }, [navigate, reportId]);
    const handleRefresh = async () => {
        if (!canRefresh || !reportId)
            return;
        setRefreshing(true);
        try {
            await dispatch(refreshCurrentReport(reportId)).unwrap();
            toast.success('Report refreshed successfully.');
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : 'Refresh failed');
        }
        finally {
            setRefreshing(false);
        }
    };
    // NEW — apply a new period from the PeriodPicker
    const handleApplyPeriod = async (range) => {
        if (!report || !canEdit)
            return;
        // Skip no-op applies
        const currentStart = new Date(report.period_start).toISOString().slice(0, 10);
        const currentEnd = new Date(report.period_end).toISOString().slice(0, 10);
        if (range.start === currentStart && range.end === currentEnd) {
            toast.info('Period is already set to those dates.');
            return;
        }
        setApplyingPeriod(true);
        try {
            const result = await dispatch(updateReportThunk({
                reportId: report.report_id,
                payload: {
                    period_start: range.start,
                    period_end: range.end,
                },
            })).unwrap();
            if (result.queued) {
                toast.success('New audit is running in the background. The page will refresh when it finishes.');
                // Re-fetch will reflect the queued job; user sees the
                // job-in-flight banner until worker completes.
                dispatch(fetchReportById(report.report_id));
            }
            else {
                toast.success('Audit re-run with new dates.');
                dispatch(fetchReportById(report.report_id));
            }
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : 'Failed to update period');
        }
        finally {
            setApplyingPeriod(false);
        }
    };
    const handleDeck = async () => {
        if (!report)
            return;
        try {
            toast.info('Preparing deck…');
            await dispatch(generateDeckThunk({
                reportId: report.report_id,
                regenerate: report.deck?.generated_at
                    ? new Date(report.updated_at) > new Date(report.deck.generated_at)
                    : false,
            })).unwrap();
            await downloadDeckBlob(report.report_id, report.client_name, authToken || '');
            toast.success('Deck downloaded');
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : err?.message || 'Deck generation failed');
        }
    };
    // ── Render guards ───────────────────────────────────────────────────
    if (loading || refreshing || applyingPeriod) {
        return (_jsx(Box, { sx: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                width: '100%',
            }, children: _jsx(CommonLoader, { className: "table-loader" }) }));
    }
    if (error) {
        return (_jsx(PageContainer, { children: _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 400,
                    gap: 1,
                }, children: [_jsx(Typography, { sx: { color: '#B42318', fontSize: 14, fontWeight: 600 }, children: error }), _jsx(Typography, { sx: {
                            color: '#475467',
                            fontSize: 12,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                        }, onClick: handleBack, children: "\u2190 Back to reports" })] }) }));
    }
    if (!report) {
        return (_jsx(PageContainer, { children: _jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }, children: _jsx(Typography, { sx: { color: '#667085', fontSize: 14 }, children: "Report not found." }) }) }));
    }
    const jobInFlight = report.job?.status === 'queued' || report.job?.status === 'processing';
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: [_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(ReportSwitcher, { clientId: String(report.client_id), reportId: report.report_id, onSwitchReport: handleSwitchReport }) }), _jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx(LastUpdatedInfo, { updatedAt: report.updated_at, onRefresh: canRefresh ? handleRefresh : undefined, disabled: !canRefresh || refreshing || jobInFlight, loading: refreshing, moduleName: MODULE_KEY, disabledReason: !canRefresh
                                        ? 'You do not have permission to refresh this report.'
                                        : jobInFlight
                                            ? 'A background job is already running for this report.'
                                            : undefined }), _jsxs(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: { xs: 'column', lg: 'row' },
                                        gap: { xs: 0.5, lg: 1.5 },
                                        alignItems: 'flex-start',
                                    }, children: [_jsx(Chip, { label: `Threshold: $${report.threshold}`, size: "small", sx: {
                                                bgcolor: '#F2F4F7',
                                                color: '#344054',
                                                fontWeight: 500,
                                                fontSize: 11,
                                                border: '1px solid #EAECF0',
                                            } }), _jsx(Chip, { label: `Benchmark: ${report.benchmark_window_days ?? 30}d`, size: "small", sx: {
                                                bgcolor: '#FCE8F2',
                                                color: '#EE2A7B',
                                                fontWeight: 600,
                                                fontSize: 11,
                                                border: '1px solid #F9C0D9',
                                            } })] })] }), rightSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx(PeriodPicker, { periodStart: report.period_start, periodEnd: report.period_end, onApply: handleApplyPeriod, disabled: !canEdit || jobInFlight, disabledReason: !canEdit
                                                ? 'You do not have permission to change the report period.'
                                                : jobInFlight
                                                    ? 'A background job is already running. Wait for it to finish before changing the period.'
                                                    : undefined, saving: applyingPeriod }), canEdit && (_jsx(AppButton, { variant: "outlined", size: "small", startIcon: _jsx(TuneIcon, {}), onClick: () => setBenchmarkDrawerOpen(true), disabled: jobInFlight, sx: {
                                                textTransform: 'none',
                                                borderColor: '#EAECF0',
                                                color: '#344054',
                                                whiteSpace: 'nowrap',
                                                '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' },
                                                height: '42px',
                                            }, children: "Custom Benchmark" })), canGenerateDeck && (_jsx(AppButton, { variant: "outlined", size: "small", startIcon: deckBusy ? null : report.deck?.file_url ? _jsx(DownloadIcon, {}) : _jsx(DeckIconSvg, {}), onClick: handleDeck, loading: deckBusy, sx: {
                                                textTransform: 'none',
                                                borderColor: '#EAECF0',
                                                color: '#344054',
                                                whiteSpace: 'nowrap',
                                                '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' },
                                                height: "42px"
                                            }, children: deckBusy
                                                ? 'Building deck…'
                                                : report.deck?.file_url
                                                    ? 'Download Deck'
                                                    : 'Generate Deck' }))] }), _jsx(TableToolbar, { title: "", showSearch: false, showRows: false, showExpand: false, showFullScreen: true })] }) }), jobInFlight && (_jsxs(Box, { sx: {
                            bgcolor: '#FFF8E1',
                            border: '1px solid #F9DBAF',
                            borderRadius: '8px',
                            px: 2,
                            py: 1,
                            fontSize: 12,
                            color: '#93370D',
                        }, children: ["This report is being", ' ', report.job?.status === 'queued' ? 'queued' : 'processed', " in the background. Data you see below may be from the previous run."] })), _jsx(Box, { sx: { pl: 0.5 }, children: _jsx(CommonTabs, { selectedValue: activeTab, onChange: (v) => setActiveTab(v), tabs: TABS }) }), _jsxs(Box, { sx: {
                            bgcolor: '#fff',
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }, children: [activeTab === 'summary' && _jsx(SummaryTab, { report: report }), activeTab === 'classifications' && _jsx(AdClassificationsTab, { report: report }), activeTab === 'fail-insights' && _jsx(FailInsightsTab, { report: report }), activeTab === 'learning' && _jsx(LearningInsightsTab, { report: report }), activeTab === 'projections' && _jsx(ProjectionsTab, { report: report }), activeTab === 'projections-v2' && _jsx(ProjectionsV2Tab, { report: report }), activeTab === 'audit-insights' && _jsx(InsightsTab, { report: report })] })] }), _jsx(CustomBenchmarkDrawer, { open: benchmarkDrawerOpen, onClose: () => setBenchmarkDrawerOpen(false), reportId: report.report_id, disabled: jobInFlight, onSaved: () => {
                    // Re-pull the report so all tabs reflect the new benchmarks
                    if (report?.report_id) {
                        dispatch(fetchReportById(report.report_id));
                    }
                } })] }));
};
export default MetaAdAuditReport;
